// Team Capacity section — dashboard parity v3.
//
// Mirrors the v1 dashboard's "Team Capacity" chart (the first
// chart under the Employee Analytics title in the old app, now the
// second chart under that title in v2 — CFD comes first per the
// parity-v1 spec). Two bars per staff member: throughput (points
// completed per week in the active window) vs demand (open
// complexity points currently assigned).
//
// Implementation note: the BE already exposes ``points`` (sum of
// completed complexity in the window) and ``open_complexity`` per
// staff via ``/api/v1/dashboard/employee_performance``. Throughput
// is just ``points / weeksInWindow``; demand is ``open_complexity``
// directly. So this section consumes the existing endpoint and
// computes the chart shape client-side — no new BE work needed.

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";

import {
  DashboardCard,
  DashboardEmptyState,
  DashboardErrorState,
  DashboardLoadingState,
  useCommonFilters,
  useDashboardDateRange,
  useEmployeePerformanceQuery,
} from "@/features/dashboard";
import {
  useCommitmentListQuery,
  type CommitmentListRow,
} from "@/features/commitments";

import { formatStaffName } from "./displayName";


export const TEAM_CAPACITY_SECTION = "team-capacity" as const;
export const TEAM_CAPACITY_SPAN_CLASS = "xl:col-span-3";


// How many staff to fetch overall. v1 paginates 10 per page; we
// match that cadence here and paginate client-side using the same
// pager footer the Workload / Blocked / Delivery charts use.
const VISIBLE_LIMIT = 50;
const PAGE_SIZE = 10;


export function TeamCapacitySection() {
  const filters = useCommonFilters();
  const dateRange = useDashboardDateRange();

  const query = useEmployeePerformanceQuery({
    ...filters,
    sortBy: "open_complexity",
    sortDir: "desc",
    limit: VISIBLE_LIMIT,
    offset: 0,
  });

  const weeks = weeksInRange(dateRange.startDate, dateRange.endDate);

  const rows = (query.data?.rows ?? []).map((r) => {
    const throughput = weeks > 0 ? r.points / weeks : 0;
    const demand = r.open_complexity;
    const loadRatio = throughput > 0 ? demand / throughput : demand > 0 ? 99 : 0;
    return {
      ...r,
      throughput: Math.round(throughput * 10) / 10,
      demand,
      loadRatio,
    };
  });

  // Max for bar scaling — drives the SVG width as a percentage.
  const maxValue = rows.reduce(
    (m, r) => Math.max(m, r.throughput, r.demand),
    1,
  );

  // Inline drill-down: which staffer's open work is expanded below
  // their row? ``null`` = nothing expanded. Independent of paging —
  // the user can leave a row expanded on a page and come back to it.
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  // Page-based pagination (matches Workload / Delivery / Blocked
  // charts). ``page`` is zero-indexed; the pager UI shows it 1-indexed.
  const [page, setPage] = useState(0);
  const pageCount = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const start = safePage * PAGE_SIZE;
  const visible = useMemo(
    () => rows.slice(start, start + PAGE_SIZE),
    [rows, start],
  );
  const showPager = rows.length > PAGE_SIZE;
  const pagerActions = showPager ? (
    <div
      data-testid="team-capacity-pager"
      className="flex items-center gap-1 text-[11px] text-text-tertiary"
    >
      <button
        type="button"
        data-testid="team-capacity-pager-prev"
        aria-label="Previous staff"
        disabled={safePage === 0}
        onClick={() => setPage((p) => Math.max(0, p - 1))}
        className="inline-flex h-6 w-6 items-center justify-center rounded text-text-secondary hover:bg-recessed disabled:cursor-not-allowed disabled:text-text-tertiary"
      >
        <ChevronLeft size={14} aria-hidden="true" />
      </button>
      <span className="tabular-nums">
        {safePage + 1} / {pageCount}
      </span>
      <button
        type="button"
        data-testid="team-capacity-pager-next"
        aria-label="Next staff"
        disabled={safePage >= pageCount - 1}
        onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
        className="inline-flex h-6 w-6 items-center justify-center rounded text-text-secondary hover:bg-recessed disabled:cursor-not-allowed disabled:text-text-tertiary"
      >
        <ChevronRight size={14} aria-hidden="true" />
      </button>
    </div>
  ) : null;

  return (
    <DashboardCard
      section={TEAM_CAPACITY_SECTION}
      className={TEAM_CAPACITY_SPAN_CLASS}
      title="Team Capacity"
      subtitle="Throughput (points completed per week) vs demand (open points). Click a staffer to peek at their open work."
      actions={pagerActions}
    >
      {query.isLoading ? (
        <DashboardLoadingState variant="card" rows={5} />
      ) : query.isError ? (
        <DashboardErrorState
          title="Couldn't load team capacity"
          description="Refresh to retry."
          retry={() => query.refetch()}
        />
      ) : rows.length === 0 ? (
        <DashboardEmptyState
          title="No staff with assigned work"
          description="Once Commitments are assigned to staff, this chart fills in."
        />
      ) : (
        <ul
          role="list"
          data-testid="team-capacity-rows"
          className="flex flex-col gap-3"
        >
          {visible.map((r) => {
            const isSelected = r.user_id === selectedUserId;
            return (
              <li
                key={r.user_id}
                data-testid={`team-capacity-row-${r.user_id}`}
                data-selected={isSelected ? "true" : "false"}
                className={[
                  "rounded-md transition-fast",
                  isSelected ? "bg-recessed/60" : "",
                ].join(" ")}
              >
                <button
                  type="button"
                  onClick={() =>
                    setSelectedUserId((prev) =>
                      prev === r.user_id ? null : r.user_id
                    )
                  }
                  data-testid={`team-capacity-link-${r.user_id}`}
                  aria-pressed={isSelected}
                  aria-expanded={isSelected}
                  className="grid w-full grid-cols-[140px_1fr_auto_auto] items-center gap-3 rounded-md px-2 py-2 text-left hover:bg-recessed/40"
                >
                  <span
                    className="truncate text-xs font-medium text-text-primary"
                    title={formatStaffName(r)}
                  >
                    {formatStaffName(r)}
                  </span>
                  <div className="flex flex-col gap-0.5">
                    <CapacityBar
                      label="Throughput"
                      value={r.throughput}
                      pct={(r.throughput / maxValue) * 100}
                      fillClass="bg-teal"
                    />
                    <CapacityBar
                      label="Demand"
                      value={r.demand}
                      pct={(r.demand / maxValue) * 100}
                      fillClass={loadFill(r.loadRatio)}
                    />
                  </div>
                  <span
                    title={`Load ratio: ${r.loadRatio.toFixed(2)}`}
                    data-testid={`team-capacity-load-${r.user_id}`}
                    className={[
                      "rounded-full px-2 py-0.5 text-[10px] font-semibold",
                      loadBadge(r.loadRatio),
                    ].join(" ")}
                  >
                    {loadLabel(r.loadRatio)}
                  </span>
                  <ChevronDown
                    size={14}
                    strokeWidth={2}
                    className={[
                      "text-text-tertiary transition-transform",
                      isSelected ? "rotate-180" : "",
                    ].join(" ")}
                    aria-hidden
                  />
                </button>
                {isSelected && (
                  <div className="px-2 pb-2">
                    <SelectedStafferWork
                      userId={r.user_id}
                      displayName={formatStaffName(r)}
                    />
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </DashboardCard>
  );
}


function CapacityBar({
  label, value, pct, fillClass,
}: {
  label: string;
  value: number;
  pct: number;
  fillClass: string;
}) {
  return (
    <div className="flex items-center gap-2">
      <span className="w-20 flex-shrink-0 text-[10px] font-medium uppercase tracking-wide text-text-tertiary">
        {label}
      </span>
      <div className="flex-1 h-3 rounded-full bg-recessed/40">
        <div
          className={["h-full rounded-full", fillClass].join(" ")}
          style={{ width: `${Math.max(0, Math.min(100, pct))}%` }}
        />
      </div>
      <span className="w-12 flex-shrink-0 text-right text-[11px] font-medium text-text-secondary tabular-nums">
        {value}
      </span>
    </div>
  );
}


function weeksInRange(startISO: string, endISO: string): number {
  const start = new Date(startISO);
  const end = new Date(endISO);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 1;
  const days = (end.getTime() - start.getTime()) / 86_400_000;
  // At minimum 1 week to avoid div-by-zero / wild scaling on
  // ranges shorter than a week.
  return Math.max(1, days / 7);
}


function loadFill(ratio: number): string {
  if (ratio >= 1.5) return "bg-ember";
  if (ratio >= 1.0) return "bg-gold";
  if (ratio >= 0.5) return "bg-sage";
  return "bg-teal/60";
}


function loadBadge(ratio: number): string {
  if (ratio >= 1.5) return "bg-ember-tint text-ember";
  if (ratio >= 1.0) return "bg-gold-tint text-gold";
  if (ratio >= 0.5) return "bg-sage-tint text-teal";
  return "bg-teal-tint text-teal";
}


function loadLabel(ratio: number): string {
  if (ratio >= 1.5) return "At risk";
  if (ratio >= 1.0) return "Stretched";
  if (ratio >= 0.5) return "On track";
  return "Underloaded";
}


// Inline drill-down: top 5 open commitments for the selected staffer.
// Sorted by due date ascending so the most-pressing rows surface
// first. A "View all in Tracker →" link routes to the filtered
// Commitment Tracker for the staffer who needs more than a peek.
//
// The list endpoint only accepts a single ``status`` (no multi-value
// filter), so we over-fetch (15 rows) and slice to the first
// ``PREVIEW_LIMIT`` open ones client-side. That's cheap for a
// per-user peek and keeps the call shape simple.
const PREVIEW_LIMIT = 5;
const FETCH_LIMIT = 15;
const CLOSED_STATUSES = new Set(["done", "cancelled"]);


function SelectedStafferWork({
  userId, displayName,
}: { userId: string; displayName: string }) {
  const query = useCommitmentListQuery({
    filters: { assigned_to: userId },
    sortBy: "due_date_time",
    sortDir: "asc",
    limit: FETCH_LIMIT,
    offset: 0,
  });

  const allRows: CommitmentListRow[] = query.data?.rows ?? [];
  const openRows = allRows.filter((r) => !CLOSED_STATUSES.has(r.status));
  const rows = openRows.slice(0, PREVIEW_LIMIT);
  const totalOpen = openRows.length;

  return (
    <div
      data-testid={`team-capacity-work-${userId}`}
      className="mt-2 flex flex-col gap-2 border-t border-border-subtle pt-2"
    >
      {query.isLoading ? (
        <p className="text-[11px] text-text-tertiary">
          Loading {displayName}'s open work…
        </p>
      ) : rows.length === 0 ? (
        <p className="text-[11px] text-text-tertiary">
          {displayName} has no open Commitments.
        </p>
      ) : (
        <ul role="list" className="flex flex-col gap-1">
          {rows.map((row) => (
            <li
              key={row.commitment_id}
              data-testid={`team-capacity-work-row-${row.commitment_id}`}
              className="grid grid-cols-[1fr_auto] items-center gap-2 text-[11px]"
            >
              <Link
                to={`/dashboard/commitments/${row.commitment_id}`}
                className="truncate text-text-primary hover:underline"
                title={row.title}
              >
                {row.title}
              </Link>
              <span className="text-text-tertiary tabular-nums">
                {formatDueLabel(row.due_date_time)}
              </span>
            </li>
          ))}
        </ul>
      )}
      <Link
        to={`/dashboard/commitment-tracker?assigned_to=${encodeURIComponent(userId)}`}
        data-testid={`team-capacity-work-view-all-${userId}`}
        className="self-start text-[11px] font-medium text-teal hover:underline"
      >
        View all in Tracker
        {totalOpen > rows.length ? ` (${totalOpen}+ open) →` : " →"}
      </Link>
    </div>
  );
}


function formatDueLabel(due: string | null | undefined): string {
  if (!due) return "—";
  const dt = new Date(due);
  if (Number.isNaN(dt.getTime())) return "—";
  return dt.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}
